import { useState, useEffect, useRef } from "react";
import { FiArrowRight, FiBook, FiBookOpen, FiMinus, FiPlus } from "react-icons/fi";
import Lenis from "lenis";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Reveal } from "./reveal";
import styles from "../style";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const FeatureAccord = ({ features }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const sectionRef = useRef(null);
    const accordionRef = useRef(null);
    const imageRef = useRef(null);
    const progressBarRef = useRef(null);
    const featureRefs = useRef([]);
    const imageContainerRef = useRef(null);

    const toggle = (index) => {
        setActiveIndex(activeIndex === index ? -1 : index);
    };

    useEffect(() => {
        // Initialize Lenis smooth scrolling
        const lenis = new Lenis({
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
        });

        lenis.on("scroll", ScrollTrigger.update);

        gsap.ticker.add((time) => {
            lenis.raf(time * 1000);
        });
        gsap.ticker.lagSmoothing(0);

        // Main scroll trigger for the accordion section
        const mainTrigger = ScrollTrigger.create({
            trigger: sectionRef.current,
            start: "top top",
            end: `+=${features.length * 100}%`,
            pin: true,
            pinSpacing: true,
            scrub: 1,
            onUpdate: (self) => {
                const progress = self.progress;
                
                // Update overall progress bar
                if (progressBarRef.current) {
                    gsap.set(progressBarRef.current, {
                        scaleY: progress,
                        transformOrigin: "top",
                    });
                }

                // Calculate which feature should be active based on scroll
                const featureProgress = progress * features.length;
                const currentIndex = Math.min(
                    Math.floor(featureProgress),
                    features.length - 1
                );
                
                // Update active index smoothly
                if (currentIndex !== activeIndex) {
                    setActiveIndex(currentIndex);
                }

                // Animate feature borders based on scroll position
                featureRefs.current.forEach((featureRef, index) => {
                    if (featureRef) {
                        const borderElement = featureRef.querySelector('.feature-border');
                        if (borderElement) {
                            const featureStart = index / features.length;
                            const featureEnd = (index + 1) / features.length;
                            const featureProgressLocal = Math.max(0, Math.min(
                                (progress - featureStart) / (featureEnd - featureStart),
                                1
                            ));
                            
                            gsap.set(borderElement, {
                                scaleX: featureProgressLocal,
                                transformOrigin: "left",
                            });
                        }
                    }
                });

                // Animate desktop image
                if (imageRef.current && imageContainerRef.current) {
                    // Subtle scale and opacity changes for the image
                    const imageScale = gsap.utils.interpolate(1, 1.1, progress);
                    const imageOpacity = gsap.utils.interpolate(0.8, 1, progress);
                    
                    gsap.set(imageRef.current, {
                        scale: imageScale,
                        opacity: imageOpacity,
                    });
                }
            }
        });

        // Animate each accordion item entrance
        featureRefs.current.forEach((featureRef, index) => {
            if (featureRef) {
                // Initial state
                gsap.set(featureRef, {
                    opacity: 0.7,
                    y: 20,
                });

                // Scroll-triggered animation for each feature
                ScrollTrigger.create({
                    trigger: featureRef,
                    start: "top center",
                    end: "bottom center",
                    onEnter: () => {
                        gsap.to(featureRef, {
                            opacity: 1,
                            y: 0,
                            duration: 0.5,
                            ease: "power2.out",
                        });
                    },
                    onLeaveBack: () => {
                        gsap.to(featureRef, {
                            opacity: 0.7,
                            y: 20,
                            duration: 0.5,
                            ease: "power2.inOut",
                        });
                    },
                });
            }
        });

        // Content expansion animation
        const expandedContents = sectionRef.current.querySelectorAll('.expanded-content');
        expandedContents.forEach((content) => {
            ScrollTrigger.create({
                trigger: content,
                start: "top bottom",
                end: "bottom top",
                onEnter: () => {
                    gsap.fromTo(content,
                        { 
                            opacity: 0, 
                            y: 30,
                            maxHeight: 0 
                        },
                        { 
                            opacity: 1, 
                            y: 0, 
                            maxHeight: 500,
                            duration: 0.6,
                            ease: "power2.out" 
                        }
                    );
                },
            });
        });

        // Cleanup
        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
            lenis.destroy();
            gsap.ticker.remove((time) => lenis.raf(time * 1000));
        };
    }, [features.length]);

    // Desktop image: falls back to first feature if none active
    const desktopImage =
        activeIndex >= 0 ? features[activeIndex]?.image : features[0]?.image;

    return (
        <div className={` w-full ${styles.flexCenter} gap-10`} ref={sectionRef}>           
            <div className="flex flex-col items-start">     
                <Reveal>
                    <h2 className=" md:pl-8 xs:text-md text-mm font-medium text-start text-black min-w-[300px] max-w-[800px]">
                        Practical support for safer, stronger school communities.
                    </h2>
                </Reveal>
                <Reveal>
                    <h2 className="md:pl-8 py-6 xs:text-mm text-sm text-start text-gray-600 min-w-[300px] max-w-[600px] ">
                        Aegis Education Group works with schools to strengthen the systems, culture and staff practice that keeps children safe. Our support is practical, school facing and shaped around each school’s context.
                    </h2>
                </Reveal>
                <Link to="/Expertise">
                    <Reveal>
                        <motion.div 
                        initial="initial"
                        whileHover="hovered"
                        className="md:pl-8 py-8 flex flex-row items-center justify-center gap-2 cursor-pointer group relative overflow-hidden"
                        >
                        {/* Arrow icon container with animation */}
                        <div className="w-[40px] h-[40px] relative mr-2 overflow-hidden">
                            <motion.div 
                            variants={{
                                initial: { y: 0 },
                                hovered: { y: "-100%" },
                            }}
                            className="absolute inset-0 bg-second flex items-center justify-center"
                            >
                            <FiArrowRight size={20} className="text-fourth" />
                            </motion.div>
                            <motion.div 
                            variants={{
                                initial: { y: "100%" },
                                hovered: { y: 0 },
                            }}
                            className="absolute inset-0 bg-fourth flex items-center justify-center"
                            >
                            <FiArrowRight size={20} className="text-second" />
                            </motion.div>
                        </div>
                        
                        {/* Text with animation */}
                        <div className="relative overflow-hidden">
                            <motion.h2 
                            variants={{
                                initial: { y: 0 },
                                hovered: { y: "-100%" },
                            }}
                            className="text-fourth xs:text-sm text-mm font-semibold"
                            >
                            Our Services
                            </motion.h2>
                            <motion.h2 
                            variants={{
                                initial: { y: "100%" },
                                hovered: { y: 0 },
                            }}
                            className="absolute inset-0 text-second xs:text-sm text-mm font-semibold"
                            >
                            Our Services
                            </motion.h2>
                        </div>
                        </motion.div>
                    </Reveal>
                </Link>

                <div className="relative flex flex-col md:flex-row md:gap-8">
                    {/* LEFT SIDE – ACCORDION LIST */}
                    <div 
                        ref={accordionRef}
                        className="flex-1 md:pl-8 py-8"
                    >
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                ref={el => featureRefs.current[index] = el}
                                className="feature-item flex flex-col items-start py-4 mb-2"
                            >
                                <div className="flex flex-col gap-2 w-full">
                                    {/* Accordion header */}
                                    <div
                                        onClick={() => toggle(index)}
                                        className="flex flex-row justify-between min-w-[300px] max-w-[600px] cursor-pointer group"
                                    >
                                        <div className="flex flex-row gap-4 items-center">
                                            <div className="relative">
                                                {activeIndex === index ? (
                                                    <FiBookOpen 
                                                        size={18} 
                                                        className="text-fourth transition-transform duration-300"
                                                    />
                                                ) : (
                                                    <FiBook 
                                                        size={18} 
                                                        className="text-secondary group-hover:text-fourth transition-colors duration-300" 
                                                    />
                                                )}
                                            </div>
                                            
                                            <h4
                                                className={`font-semibold text-fourth xs:text-mm text-sm leading-[23.4px] mb-1 transition-all duration-300 ${
                                                    activeIndex === index 
                                                        ? "text-fourth scale-105" 
                                                        : "text-secondary group-hover:text-fourth "
                                                }`}
                                            >
                                                {feature.title}
                                            </h4>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {activeIndex === index ? (
                                                <FiMinus size={18} className="text-fourth" />
                                            ) : (
                                                <FiPlus size={18} className="text-secondary" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Expanded content with animation */}
                                    <div 
                                        className={`expanded-content transition-all duration-500 overflow-hidden ${
                                            activeIndex === index 
                                                ? "max-h-[500px] opacity-100" 
                                                : "max-h-0 opacity-0"
                                        }`}
                                    >
                                        {activeIndex === index && (
                                            <>
                                                <p className="xs:text-sm text-ss text-start pt-2 max-w-[460px]">
                                                    {feature.content}
                                                </p>

                                                {/* Mobile image */}
                                                <div className="mt-4 md:hidden">
                                                    <img
                                                        src={feature.image}
                                                        alt={feature.title}
                                                        className="w-[460px] h-[300px] object-cover rounded-lg shadow-md"
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* Animated border as progress indicator */}
                                    <div className="feature-border relative w-full h-[2px] bg-gray-100 overflow-hidden">
                                        <div 
                                            className="absolute inset-0 bg-second origin-left"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* RIGHT SIDE – DESKTOP/TABLET SHARED IMAGE */}
                    <div 
                        ref={imageContainerRef}
                        className="md:w-[400px] md:sticky mt-10 hidden md:block"
                    >
                        <div className="relative overflow-hidden rounded-lg shadow-md">
                            <img
                                ref={imageRef}
                                src={desktopImage}
                                alt="Current feature"
                                className="w-full h-[400px] object-cover"
                            />
                            {/* Image overlay effect */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

    );
};

export default FeatureAccord;